import React, { useState } from 'react';
import { requestOtp, getToken, startPollingAfterLogin } from '../../api/client';

interface LoginModalProps {
  isOpen: boolean;
  onLoginSuccess: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onLoginSuccess }) => {
  const [email, setEmail] = useState(''); // No default email
  const [otp, setOtp] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleRequestOtp = async () => {
    setIsLoading(true);
    setMessage('');
    try {
      await requestOtp(email);
      setOtpRequested(true);
      setMessage('OTP sent! Check your email for the verification code.');
    } catch (error: any) {
      console.error('Error requesting OTP:', error);

      // Extract error message from axios response
      let errorMessage = 'Failed to request OTP.';
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.status === 404) {
        errorMessage = 'Failed: user not found.';
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setMessage('');
    try {
      const token = await getToken(email, otp);
      localStorage.setItem('accessToken', token.access_token);

      // Start polling for user status changes
      startPollingAfterLogin();

      onLoginSuccess();
    } catch (error) {
      console.error('Error logging in:', error);
      setMessage('Login failed. Invalid email or OTP.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setOtpRequested(false);
    setOtp('');
    setMessage('');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (!otpRequested) {
        handleRequestOtp();
      } else {
        handleLogin();
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '40px',
    zIndex: 1001, // Higher than ProjectBrowserModal
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
    minWidth: '400px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  };

  const inputStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    width: '100%',
    fontSize: '16px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    fontFamily: 'inherit',
    fontWeight: '500',
    transition: 'background-color 0.2s ease',
    minWidth: '120px',
    width: '100%',
  };

  return (
    <div style={modalStyle}>
      {/* Logo */}
      <img
        src="/full_logo.png"
        alt="Quanergy Logo"
        style={{
          width: '100%',
          height: 'auto',
          marginBottom: '10px',
          objectFit: 'contain',
          maxHeight: '120px'
        }}
      />

      {/* App Title */}
      <h1 style={{
        margin: '0 0 8px 0',
        fontSize: '28px',
        color: '#1f2937',
        textAlign: 'center',
        fontFamily: "'Lato', sans-serif",
        fontWeight: '900'
      }}>
        Q-DESIGN
      </h1>

      {/* Subtitle */}
      <p style={{
        margin: '0 0 24px 0',
        fontSize: '16px',
        color: '#6b7280',
        textAlign: 'center'
      }}>
        Log into your account
      </p>

      {message && (
        <p style={{
          color: message.startsWith('Failed') ? '#dc2626' : '#059669',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          {message}
        </p>
      )}

      {/* Email Input */}
      <div style={{ width: '100%', marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#374151'
        }}>
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter your email address"
          style={inputStyle}
          disabled={otpRequested || isLoading}
        />
      </div>

      {!otpRequested && (
        <button onClick={handleRequestOtp} style={buttonStyle} disabled={isLoading}>
          {isLoading ? 'Sending...' : 'Request OTP'}
        </button>
      )}

      {otpRequested && (
        <>
          <div style={{ width: '100%', marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Verification Code
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter verification code"
              style={inputStyle}
              disabled={isLoading}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
            <button onClick={handleLogin} style={buttonStyle} disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
            <button
              onClick={handleBack}
              style={{
                ...buttonStyle,
                backgroundColor: '#6b7280',
                minWidth: '80px'
              }}
              disabled={isLoading}
            >
              Back
            </button>
          </div>
          <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
            <span style={{ color: '#6b7280' }}>Didn't receive code? </span>
            <button
              onClick={handleRequestOtp}
              style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit',
                fontWeight: '500',
                textDecoration: 'none'
              }}
              disabled={isLoading}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Resend
            </button>
          </div>
        </>
      )}

      {/* Support Text */}
      <p style={{
        margin: '24px 0 0 0',
        fontSize: '14px',
        color: '#6b7280',
        textAlign: 'center',
        lineHeight: '1.4'
      }}>
        For assistance or to request an account,<br />
        contact <a href="mailto:support@quanergy.com" style={{ color: '#3b82f6', textDecoration: 'none' }}>support@quanergy.com</a>
      </p>
    </div>
  );
};

export default LoginModal;